import {
  Button,
  FileInputControlled,
  Modal,
  Select,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Form, Row } from 'reactstrap';

import { TranslationsKeys } from '../../../locales/translations';
import { uploadBenefitFile } from '../api';
import { BenefitKind, UploadBenefitErrorBody } from '../types';

interface Props {
  isOpen: boolean;
  benefitKind: BenefitKind | null;
  academicYears: number[];
  onClose: () => void;
  onSuccess: () => void;
  onInvalidFormat: () => void;
  onInvalidData: (fileUploadFailureId: number | string) => void;
}

/**
 * Se arma sobre el `Modal` base (y no sobre `components/modals/UploadModal`)
 * porque ese componente sólo inyecta el `control` de react-hook-form en los
 * hijos de tipo `UploadInput`/`UploadTextArea`, y acá hace falta un `Select`
 * de año académico también enlazado al formulario.
 */
export default function UploadBenefitModal({
  isOpen,
  benefitKind,
  academicYears,
  onClose,
  onSuccess,
  onInvalidFormat,
  onInvalidData,
}: Props) {
  const { t } = useTranslation(
    TranslationsKeys.ADMISSION_AND_ENROLLMENTS_BENEFIT_ALLOCATION,
  );
  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ mode: 'onSubmit' });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const requiredFieldMsg = String(t('uploadModal.requiredField'));

  const academicYearOptions = useMemo(
    () => academicYears.map((year) => ({ label: String(year), value: year })),
    [academicYears],
  );

  const onSubmit = useCallback(
    async (values: any) => {
      if (!benefitKind) {
        return;
      }
      const { error } = await uploadBenefitFile({
        code: benefitKind.code,
        academicYear: Number(values.academicYear?.value),
        file: values.file as File,
      });

      if (!error) {
        onSuccess();
        return;
      }

      const body = (error.data ?? {}) as UploadBenefitErrorBody;
      if (body.code === 'INVALID_FORMAT') {
        onInvalidFormat();
        return;
      }
      if (body.code === 'INVALID_DATA' && body.fileUploadFailureId) {
        onInvalidData(body.fileUploadFailureId);
        return;
      }
      addToast({
        icon: 'error',
        color: 'danger',
        text: body.message ?? t('errors.generic'),
      });
    },
    [benefitKind, onSuccess, onInvalidFormat, onInvalidData, t],
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <Container fluid>
        <p className="text-dark fs-20 mt-3 mb-4 text-center">
          {benefitKind ? t(`kinds.${benefitKind.code}.modalTitle`) : ''}
        </p>
        <p className="text-dark fs-16 mt-3 mb-4 text-center">
          {t('uploadModal.subtitle')}
        </p>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-4">
            <Col xs={12}>
              <Select
                name="academicYear"
                label={t('uploadModal.academicYear')}
                options={academicYearOptions}
                control={control}
                isClearable={false}
                disabled={isSubmitting}
                rules={{ required: requiredFieldMsg }}
              />
            </Col>
            <Col xs={12}>
              <FileInputControlled
                name="file"
                label={t('uploadModal.file')}
                btnText={t('table.upload')}
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                control={control}
                disabled={isSubmitting}
                rules={{ required: requiredFieldMsg }}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6} className="pb-2">
              <Button
                onClick={onClose}
                text={t('uploadModal.cancel')}
                outlined
                fullwidth
                disabled={isSubmitting}
              />
            </Col>
            <Col xs={12} sm={6} className="pb-2">
              <Button
                type="submit"
                text={t('uploadModal.submit')}
                fullwidth
                loading={isSubmitting}
              />
            </Col>
          </Row>
        </Form>
      </Container>
    </Modal>
  );
}
